/**
 * IOSInstallConfirmToast
 * Mini-toast discreto que pregunta al usuario si ya instalo la PWA en iPhone.
 * Refuerza la metrica de conversion sumando "confirmaciones manuales" a las
 * detecciones automaticas de standalone (pwa_installed).
 *
 * Reglas:
 *  - Solo iOS Safari (misma detecion que el banner)
 *  - No standalone
 *  - Usuario toco el CTA del banner en los ultimos 7 dias
 *  - No confirmo manualmente antes
 *  - Maximo 1 vez por sesion
 *  - Maximo 3 sesiones totales
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { trackPwa } from '../utils/trackPwa';

const CTA_AT_KEY = 'damyxia-pwa-cta-at';
const CONFIRMED_KEY = 'damyxia-pwa-manual-confirmed';
const SESSIONS_SHOWN_KEY = 'damyxia-pwa-confirm-sessions-shown';
const SESSION_FLAG_KEY = 'damyxia-pwa-confirm-shown-this-session';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_SESSIONS = 3;
const SHOW_AFTER_MS = 8000;

const isIOSSafari = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (!isIOS) return false;
  if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA|YaBrowser/.test(ua)) return false;
  return true;
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const IOSInstallConfirmToast = () => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const txt = {
    es: {
      message: '¿Ya instalaste DamyxIA? Toca aquí',
      confirm: 'Confirmar',
      close: 'Cerrar',
      thanks: '¡Gracias por confirmar!',
    },
    en: {
      message: 'Already installed DamyxIA? Tap here',
      confirm: 'Confirm',
      close: 'Close',
      thanks: 'Thanks for confirming!',
    },
  };
  const t = txt[language] || txt.es;

  useEffect(() => {
    if (!isIOSSafari()) return;
    if (isStandalone()) return;

    let ctaAt, confirmed, sessionsShown, sessionFlag;
    try {
      ctaAt = Number(localStorage.getItem(CTA_AT_KEY) || 0);
      confirmed = localStorage.getItem(CONFIRMED_KEY) === 'true';
      sessionsShown = Number(localStorage.getItem(SESSIONS_SHOWN_KEY) || 0);
      sessionFlag = sessionStorage.getItem(SESSION_FLAG_KEY) === '1';
    } catch {
      return;
    }

    if (!ctaAt) return; // usuario nunca abrio el modal de install
    if (Date.now() - ctaAt > SEVEN_DAYS_MS) return; // expiro la ventana
    if (confirmed) return; // ya confirmo
    if (sessionsShown >= MAX_SESSIONS) return; // agotadas las 3 sesiones
    if (sessionFlag) return; // ya se mostro en esta sesion

    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem(SESSION_FLAG_KEY, '1');
        localStorage.setItem(SESSIONS_SHOWN_KEY, String(sessionsShown + 1));
      } catch {}
      setVisible(true);
    }, SHOW_AFTER_MS);

    return () => clearTimeout(timer);
  }, []);

  const handleConfirm = () => {
    if (confirming) return;
    setConfirming(true);
    try {
      localStorage.setItem(CONFIRMED_KEY, 'true');
    } catch {}
    trackPwa('pwa_installed_manual_confirm', { source: 'manual_toast' });
    // feedback visual breve y cerrar
    setTimeout(() => setVisible(false), 1200);
  };

  const handleClose = () => {
    setVisible(false);
  };

  if (!visible) return null;

  return createPortal(
    <div
      className="fixed left-3 right-3 z-[10001] flex items-center gap-3 rounded-2xl border border-emerald-400/30 bg-zinc-900/95 px-4 py-2.5 shadow-xl shadow-black/40 backdrop-blur-md animate-in fade-in slide-in-from-bottom-4"
      style={{ bottom: 'calc(12px + env(safe-area-inset-bottom))' }}
      data-testid="ios-install-confirm-toast"
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
      </div>
      <p className="flex-1 min-w-0 text-xs text-zinc-200 truncate">
        {confirming ? t.thanks : t.message}
      </p>
      {!confirming && (
        <>
          <button
            onClick={handleConfirm}
            className="flex-shrink-0 px-3 py-1 bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-bold rounded-full active:scale-95 transition-all"
            data-testid="ios-install-confirm-btn"
          >
            {t.confirm}
          </button>
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-1 text-zinc-500 hover:text-zinc-200 rounded transition-colors"
            aria-label={t.close}
            data-testid="ios-install-confirm-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </div>,
    document.body
  );
};

export default IOSInstallConfirmToast;
